<template>
  <div class="landingHeaderSection container">
    <SwiperSlider ref="mySwiper" :options="swiperOptions" class="landingHeaderSection__swiper">
      <a
        v-for="(banner, index) in data.Banners"
        :key="index"
        :href="banner.Url"
        class="landingHeaderSection__slide swiper-slide"
      >
        <div class="landingHeaderSection__inner">
          <div v-if="banner" class="landingHeaderSection__image">
            <picture>
              <source
                media="(max-width: 599px)"
                :srcset="
                  banner.MobileBanner
                    ? $helper.optimizeImageUrl(JSON.parse(banner.MobileBanner).url, 'w_800,h_620')
                    : $helper.optimizeImageUrl(JSON.parse(banner.Banner).url, 'w_800,h_620')
                "
              />
              <source
                media="(min-width: 600px)"
                :srcset="$helper.optimizeImageUrl(JSON.parse(banner.Banner).url, 'w_1400,h_350')"
              />
              <img
                :src="$helper.optimizeImageUrl(JSON.parse(banner.Banner).url, 'w_1400,h_350')"
                :alt="banner.Title"
              />
            </picture>
          </div>

          <div class="landingHeaderSection__content">
            <h1 v-if="banner.Title" class="landingHeaderSection__contentTitle">
              {{ banner.Title }}
            </h1>
            <div v-if="banner.LinkText && banner.Url" class="landingHeaderSection__contentButton">
              <VButton
                class="landingHeaderSection__slideButton"
                :text="banner.LinkText"
                theme="purple"
                type="submit"
              />
            </div>
          </div>
        </div>
      </a>
    </SwiperSlider>
    <div ref="pagination" class="landingHeaderSection__pagination swiper-pagination" />
  </div>
</template>

<script>
import NewImageBox from '@/components/NewImageBox.vue';
import VButton from '@/components/VButton.vue';
import SwiperSlider from '@/components/SwiperSlider.vue';

export default {
  name: 'LandingHeaderSection',
  components: { SwiperSlider, VButton, NewImageBox },
  props: {
    data: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      swiperOptions: {
        slidesPerView: 'auto',
        loop: false,
        speed: 800,
        spaceBetween: 0,
        lazy: {
          loadPrevNext: true,
        },
        autoplay: {
          delay: 4000,
          disableOnInteraction: false,
        },
        pagination: {
          el: '.landingHeaderSection__pagination',
          clickable: true,
          bulletClass: 'landingHeaderSection__paginationBullet',
          bulletActiveClass: 'landingHeaderSection__paginationBullet--active',
          type: 'bullets',
          renderBullet: (index, className) => {
            return `<span class="${className} active"><span class="landingHeaderSection__paginationText">${
              index + 1
            }</span><svg width="28" height="28" class="landingHeaderSection__paginationSvg">
            <circle class="landingHeaderSection__paginationCircle" cx="14" cy="14" r="13" stroke="#FFFFFF" stroke-width="2" fill="none"/>
            </svg></span>`;
          },
        },
      },
    };
  },
};
</script>

<style lang="scss">
.landingHeaderSection {
  $root: &;
  position: relative;
  margin-bottom: 1.6rem;

  &__inner {
    display: flex;
    align-items: center;
    position: relative;
    padding: 2rem 4rem;
    height: 100%;
    width: 100%;
    aspect-ratio: 1400 / 200;

    @include rwd('small-tablet') {
      aspect-ratio: 800 / 620;
    }

    @include rwd('large-tablet') {
      padding: 2rem 2rem 4rem;
    }
  }

  &__slide {
    height: auto;

    &:hover {
      #{$root}__slideButton {
        background-color: $cMainHover;
        box-shadow: 0 6px 12px rgba(15, 2, 34, 0.2);
      }
    }
  }

  &__image {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;

    img {
      object-fit: cover;
      width: 100%;
      height: 100%;
    }
  }

  &__content {
    position: relative;
    z-index: 1;
    display: flex;
    justify-content: space-between;
    gap: 2rem;
    width: 100%;

    @include rwd('tablet') {
      flex-direction: column;
      gap: 1.5rem;
    }
  }

  &__contentTitle {
    font-size: 4.5rem;
    line-height: 5.5rem;
    font-weight: 600;
    color: $cWhite;

    @include rwd('laptop') {
      font-size: 4rem;
      line-height: 5rem;
    }

    @include rwd('large-tablet') {
      font-size: 3.5rem;
      line-height: 4.2rem;
    }

    @include rwd('tablet') {
      font-size: 3rem;
      line-height: 3.6rem;
    }
  }

  &__pagination {
    left: 5rem !important;
    right: auto !important;
    bottom: 1rem !important;
    width: auto !important;

    @include rwd('tablet') {
      right: 50% !important;
      bottom: 0 !important;
      transform: translateX(50%);
    }
  }
  &__paginationBullet {
    display: inline-block;
    position: relative;
    width: 2.8rem;
    height: 2.8rem;
    cursor: pointer;

    @include rwd('tablet') {
      width: 2rem;
    }

    &:before {
      content: '';
      display: block;
      width: 7px;
      height: 7px;
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      border-radius: 50%;
      background-color: rgba($cWhite, 0.5);
      opacity: 1;
      transition: opacity $tr;

      @include rwd('tablet') {
        width: 8px;
        height: 8px;
        border-radius: 50px;
        background-color: $cAddLight;
        transition: width $tr;
      }
    }

    &:first-child {
      #{$root}__paginationCircle {
        animation-name: circleAnimation;
        animation-duration: 7.8s;
      }
    }

    &--active {
      #{$root}__paginationCircle {
        stroke-dashoffset: 100px;
        transition: linear 5.8s stroke-dashoffset;
      }

      &:before {
        opacity: 0;

        @include rwd('tablet') {
          opacity: 1;
          width: 22px;
          background-color: $cAddDark;
        }
      }

      #{$root}__paginationText,
      #{$root}__paginationSvg {
        opacity: 1;
      }
    }
  }

  &__paginationText {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    font-size: 1.2rem;
    line-height: 2rem;
    color: $cWhite;
    font-weight: 700;
    opacity: 0;
    transition: opacity $tr;

    @include rwd('tablet') {
      display: none;
    }
  }

  &__paginationSvg {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%) rotate(-90deg);
    opacity: 0;
    stroke-dashoffset: 200px;

    @include rwd('tablet') {
      display: none;
    }
  }

  &__paginationCircle {
    stroke-dasharray: 200px;
    transition: opacity $tr;
  }
  @keyframes circleAnimation {
    0% {
      stroke-dashoffset: 200px;
    }
    100% {
      stroke-dashoffset: 100px;
    }
  }
}
</style>
